import { useCallback, useMemo, useState } from "react"
import {
	IconOffice3Fill18 as Building2,
	IconLayers3Fill18 as Layers,
	IconUserPenFill18 as UserPen,
} from "nucleo-ui-fill-18"

import type { NestedCommandGroup } from "@productlane/ui/src/components/internal/nested-command"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { FilterType } from "./filter-popover-items/types"
import { FilterPopover } from "@/components/filters"
import { useContactsFilterState } from "./contacts-filter-state"
import { CompanyFilterItems } from "./filter-popover-items/company-filter-items"
import { SourceFilterItems } from "./filter-popover-items/source-filter-items"
import { SubscriptionFilterItems } from "./filter-popover-items/subscription-filter-items"
import {
	useContactsSearchableItems,
	useFilteredContactsSearchItems,
} from "./use-contacts-searchable-items"

export const FILTER_CATEGORIES: NestedCommandGroup<FilterType>[] = [
	{ type: "Companies", label: "Company", Icon: Building2 },
	{ type: "Subscription", label: "Subscription", Icon: UserPen },
	{ type: "CreatedBy", label: "Synced with", Icon: Layers },
]

const CATEGORIES_WITH_CUSTOM_FILTER: FilterType[] = ["Companies"]

function FilterSubPanel({
	activeFilter,
	query,
	setQuery,
	onFilterItemSelect,
}: {
	activeFilter: FilterType
	query: string
	setQuery: (q: string) => void
	onFilterItemSelect: () => void
}) {
	const hasCustomFilter = CATEGORIES_WITH_CUSTOM_FILTER.includes(activeFilter)
	return (
		<Command shouldFilter={!hasCustomFilter}>
			<CommandInput
				key={activeFilter}
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[min(70dvh,320px)]">
				<CommandEmpty>No results found.</CommandEmpty>
				<CommandGroup>
					{activeFilter === "Companies" && (
						<CompanyFilterItems query={query} onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Subscription" && (
						<SubscriptionFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "CreatedBy" && (
						<SourceFilterItems onSelect={onFilterItemSelect} />
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

export function ContactsFilter() {
	const [globalQuery, setGlobalQuery] = useState("")
	const filterState = useContactsFilterState()

	const counts = useMemo(
		() =>
			({
				Companies: filterState.companyIds.length,
				Subscription: filterState.subscribedStates.length,
				CreatedBy: filterState.sources.length,
			}) as Record<FilterType, number>,
		[filterState],
	)

	const onCategorySelect = useCallback(() => {
		setGlobalQuery("")
	}, [])

	const searchableItems = useContactsSearchableItems({
		onCategorySelect: onCategorySelect as () => void,
		query: globalQuery,
	})
	const filteredSearchItems = useFilteredContactsSearchItems(
		globalQuery,
		searchableItems,
	)

	const renderSubPanel = useCallback(
		(
			group: FilterType,
			query: string,
			setQuery: (q: string) => void,
			onFilterItemSelect: () => void,
		) => (
			<FilterSubPanel
				activeFilter={group}
				query={query}
				setQuery={setQuery}
				onFilterItemSelect={onFilterItemSelect}
			/>
		),
		[],
	)

	return (
		<FilterPopover<FilterType>
			categories={FILTER_CATEGORIES}
			counts={counts}
			renderSubPanel={renderSubPanel}
			searchableItems={searchableItems}
			filteredSearchItems={filteredSearchItems}
			onGlobalQueryChange={setGlobalQuery}
			tooltipLabel="Filter people"
		/>
	)
}
